import type { AnalyticsBrowser } from '@segment/analytics-next';
import { requestIdleCallback as requestIdleCallbackPolyfill } from './internal/requestIdleCallback';

export interface SegmentAbstraction {
  segmentInstance: AnalyticsBrowser;
  page(...args: Parameters<AnalyticsBrowser['page']>): ReturnType<AnalyticsBrowser['page']>;
  track(...args: Parameters<AnalyticsBrowser['track']>): ReturnType<AnalyticsBrowser['track']>;
  trackNow(...args: Parameters<AnalyticsBrowser['track']>): ReturnType<AnalyticsBrowser['track']>;
  identify(
    ...args: Parameters<AnalyticsBrowser['identify']>
  ): ReturnType<AnalyticsBrowser['identify']>;
}

const requestIdleCallback =
  typeof window !== 'undefined' &&
  'requestIdleCallback' in window &&
  typeof window.requestIdleCallback === 'function'
    ? window.requestIdleCallback.bind(window)
    : requestIdleCallbackPolyfill;

function deferWithIdleCallback<T>(fn: () => T | Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    requestIdleCallback(
      () => {
        try {
          const result = fn();
          if (result instanceof Promise) {
            result.then(resolve).catch(reject);
          } else {
            resolve(result as T);
          }
        } catch (error) {
          reject(error);
        }
      },
      // No timeout - best-effort execution only when idle
    );
  });
}

export class SegmentLive implements SegmentAbstraction {
  public readonly page: AnalyticsBrowser['page'];
  public readonly track: AnalyticsBrowser['track'];
  public readonly trackNow: AnalyticsBrowser['track'];
  public readonly identify: AnalyticsBrowser['identify'];

  constructor(private readonly analytics: AnalyticsBrowser) {
    this.page = this.createDeferredMethod((...args) => this.analytics.page(...args));
    this.track = this.createDeferredMethod((...args) => this.analytics.track(...args));
    this.trackNow = (...args) => this.analytics.track(...args);
    this.identify = this.createDeferredMethod((...args) => this.analytics.identify(...args));
  }

  get segmentInstance() {
    return this.analytics;
  }

  /**
   * Note: Uses `any[]` in the constraint because TypeScript requires parameter types in function
   * type constraints, and `unknown[]` causes contravariance errors. The `any[]` is only used
   * in the constraint; actual parameter types are preserved via `Parameters<T>`, maintaining
   * full type safety.
   */
  // biome-ignore lint/suspicious/noExplicitAny: see comment above
  private createDeferredMethod<T extends (...args: any[]) => Promise<unknown>>(method: T): T {
    return ((...args: Parameters<T>) => {
      return deferWithIdleCallback(() => method(...args));
    }) as T;
  }
}

export class SegmentMock implements SegmentAbstraction {
  public readonly page: AnalyticsBrowser['page'];
  public readonly track: AnalyticsBrowser['track'];
  public readonly trackNow: AnalyticsBrowser['track'];
  public readonly identify: AnalyticsBrowser['identify'];

  constructor() {
    this.page = this.createMockMethod<AnalyticsBrowser['page']>('page_view');
    this.track = this.createMockMethod<AnalyticsBrowser['track']>('track');
    this.trackNow = this.track;
    this.identify = this.createMockMethod<AnalyticsBrowser['identify']>('identify');
  }

  get segmentInstance() {
    return {
      track: this.track,
      trackNow: this.trackNow,
      identify: this.identify,
      page: this.page,
    } as unknown as AnalyticsBrowser;
  }

  /**
   * Creates a mock method that logs the call and returns a resolved promise.
   *
   * Note: Returns Promise.resolve with an empty object cast to the return type.
   * In production, AnalyticsBrowser methods return Promise<Context> where Context
   * contains event tracking information. For mock/debug purposes, this empty object
   * satisfies the type system but consumers should not rely on the resolved value.
   */
  // biome-ignore lint/suspicious/noExplicitAny: see comment above
  private createMockMethod<T extends (...args: any[]) => Promise<unknown>>(methodName: string): T {
    return ((...args: Parameters<T>) => {
      console.log(`ANALYTICS ${methodName.toUpperCase()}`, ...args);
      // Return type is Promise<Context> in production, but for mock we return empty object
      // Consumers should not rely on the resolved value in mock mode
      return Promise.resolve({} as Awaited<ReturnType<T>>) as unknown as ReturnType<T>;
    }) as unknown as T;
  }
}
